import React, {lazy, memo, useEffect, useState, Suspense} from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

/* Components */
import Button from '@cosmos/button'
import Carousel from '@cosmos/carousel'

/* Utils */
import {handleViewEvent, promoCodeCouponsViewEvent} from '../../../../modules/product/productCommonUtils'
import {generateReferrerUrl} from '../../../../common/url/urlUtils'
import appConfig, {loginUrl} from '../../../../appConfig'

/* Styles */
import s from './merchant-coupons.scss'

/* Lazy Components */
const Modal = lazy(() => import('./modal/Modal'))
const MerchantCouponDetail = lazy(() => import('./modal/MerchantCouponDetail'))

dayjs.extend(utc)

const CampaignType = {
  DISCOUNT_RATE: 1,
  DISCOUNT_AMOUNT: 2,
  FIXED_PRICE: 3
}

export const CouponTypes = {
  PROMO_CODE: 0,
  MERCHANT_COUPON: 1,
  GIFT_CERT: 2,
  UNIQUE_CODE: 3,
  GOLDEN_COUPON: 4
}

const MerchantCoupons = memo(({
  className,
  sku,
  merchantId,
  userInfo
}) => {
  const [couponList, setCouponList] = useState(null)
  const [isCouponDetailModalOpen, setIsCouponDetailModalOpen] = useState(false)
  const [selectDetailContent, setSelectDetailContent] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectCoupon, setSelectCoupon] = useState(false)
  const [modalContent, setModalContent] = useState(null)

  useEffect(() => {
    getMerchantCoupon(sku, merchantId)
  }, [sku, merchantId])

  async function getMerchantCoupon() {
    const url = `${appConfig.campaignApiUrl}/v1/coupons/pdp?sku=${sku}&merchantId=${merchantId}`

    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`)
      }

      const json = await response.json()

      setCouponList(json?.coupons)
      setTimeout(() => {
        handleViewEvent(json?.coupons)

        if (json?.coupons && json?.coupons?.length && json?.coupons?.filter?.(item => item.couponType !== 0)?.length) {
          const categoryNameHierarchy = window?.utagData?.category_name_hierarchy
          const categoryIdHierarchy = window?.utagData?.category_id_hierarchy

          promoCodeCouponsViewEvent(
            json?.coupons?.filter(item => item.couponType !== 0),
            sku,
            categoryNameHierarchy,
            categoryIdHierarchy
          )
        }
      }, 1000)
    } catch (error) {
      console.error('Error get campaign-coupon api', error.message)
    }
  }

  async function sendCollect(couponId, couponType) {
    const url = `${appConfig.campaignApiUrl}/v1/merchantCoupons/collect`
    setSelectCoupon(couponList.find(coupon => coupon.couponId === couponId))

    try {
      const postResponse = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({couponId}),
      })

      if(postResponse.status === 401) {
        const redirectUrl = generateReferrerUrl(`${loginUrl}/uyelik/giris`, window.location.href)
        window.location.href = redirectUrl
        return;
      }

      const responseData = await postResponse.json()

      if (!responseData?.success) {
        setIsModalOpen(true)
        setModalContent(responseData)
        throw new Error(`Response status: ${postResponse.status}`)
      }

      if (responseData?.success) {
        setModalContent(responseData)
        setIsModalOpen(true)
        handleFollowAndWinEvent(couponType)

        setCouponList(couponList.map(coupon =>
          coupon.couponId === couponId ? {...coupon, isCollected: true} : coupon
        ))
      }
    } catch (error) {
      console.error('Error with campaign-coupon API', error.message)
    }
  }

  const handleFollowAndWinEvent = ({couponType}) => {
    window?.hepsiBus?.track('followWinClick', {
      location: 'ProductDetailPage',
      page_value: '',
      page_type: 'pdp',
      text: couponType === 0 ? 'Takip et, kazan' : 'Kazan',
      sku: window?.productModel?.product?.sku || '',
    })
  }

  const handleCollectButton = (couponId, couponType) => {
    if (userInfo?.isAuthenticated) {
      sendCollect(couponId, couponType)
    } else {
      const redirectUrl = generateReferrerUrl(`${loginUrl}/uyelik/giris`, window.location.href)
      window.location.href = redirectUrl
    }
  }

  const handleDetailButton = (item) => {
    setSelectDetailContent(item)
    setIsCouponDetailModalOpen(true)
  }

  const renderCouponStatus = (isCollected, couponType, currentCount, remainingDaysCount) => {
    if (isCollected || couponType === 3) { //type 3 golden coupon
      return <></>
    }

    if (!isCollected && currentCount && currentCount <= 99) {
      return <span className={s.remainings}>{`Son ${currentCount} kupon`}</span>
    }

    if (!isCollected && currentCount > 99 && remainingDaysCount && remainingDaysCount <= 3) {
      return (
        <div className={s.remainings}>
          <div className={s.historyIcon}></div>
          <div>
            {remainingDaysCount === 1 ? 'Son gün' : `Son ${remainingDaysCount} gün`}
          </div>
        </div>
      )
    }

    return <></>
  }

  const renderCouponDiscount = (coupon) => {
    let isPromoCode = coupon?.couponType === CouponTypes.PROMO_CODE
    let isCouponHalfFromUs = coupon?.isCouponHalfFromUs

    if (isPromoCode && coupon?.campaignType === CampaignType.DISCOUNT_RATE) {
      return (
        <>
          <span>{'%'}{coupon?.discount}</span>
          {!isCouponHalfFromUs && <span className={s.priceText}>indirim</span>}
        </>
      )
    }

    return (
      <>
        <span>{coupon?.discount}</span><span className={s.priceText}>TL</span>
      </>
    )
  }

  const carouselProps = {
    className: '',
    nextArrowClassname: s.nextArrow,
    prevArrowClassname: s.prevArrow,
  }

  const divProps = {
    className: s.merchantCarouselContainer
  }

  const WrapperComponent = couponList?.length > 2 ? Carousel : 'div'
  const WrapperComponentProps = couponList?.length > 2 ? carouselProps : divProps

  if (!couponList) {
    return <div></div>
  }

  return (
    <>
      <div className={cx(s.root, className)} data-test-id={'merchant-coupons'}>
        <div className={s.couponsTitle}>{'Kuponlar'}</div>
        {
          couponList?.length > 0 &&
          <WrapperComponent {...WrapperComponentProps}>
            {
              couponList?.map?.((item, index) => {
                return (
                  <div key={index} className={cx(s.container, {[s.isCollectedContainer]: item?.isCollected})}>
                    {renderCouponStatus(item?.isCollected, item?.couponType, item?.currentCount, item?.remainingDaysCount)}
                    <div className={s.top}>
                      <div>
                        <div className={s.price}>
                          {renderCouponDiscount(item)}
                        </div>
                        <div className={s.limitInfo}>Alt limit: {item?.conditionAmount} TL</div>
                      </div>
                      <div>
                        <Button
                          className={cx(s.actionButton, {[s.isCollected]: item?.isCollected})}
                          onClick={item.isCollected ? null : () => handleCollectButton(item.couponId, item?.couponType)}>
                          {item.isCollected ?
                            <div className={s.collectedButtonContainer}>
                              <div>Kazandın</div>
                              <div className={s.tick}></div>
                            </div>
                            : item?.couponSubType === 0 ? 'Takip et, kazan' : 'Kazan'}
                        </Button>
                      </div>
                    </div>
                    <div className={s.separator}></div>
                    <div className={s.bottom}>
                      <div className={s.endDate}>
                        {dayjs(item?.endDateTime).utc(true).
                          format('[Geçerlilik ] DD.MM.YYYY HH:mm')}
                      </div>
                      <div className={s.detailButton} onClick={() => handleDetailButton(item)}>{'Detaylar'}</div>
                    </div>
                  </div>
                )
              })
            }
          </WrapperComponent>
        }
      </div>
      {
        process.env.BROWSER && isCouponDetailModalOpen &&
        <Suspense fallback={<div/>}>
          <MerchantCouponDetail
            isMerchantCouponType={selectDetailContent?.couponType === 1}
            selectDetailContent={selectDetailContent}
            closeButton={setIsCouponDetailModalOpen}/>
        </Suspense>
      }
      {
        process.env.BROWSER && isModalOpen &&
        <Suspense fallback={<div/>}>
          <Modal
            isSuccess={modalContent?.success}
            closeButton={setIsModalOpen}
            modalContent={modalContent}
            customerDailyCouponsLimit={modalContent?.customerDailyCouponsLimit}
            couponDiscountAmount={modalContent?.couponDiscountAmount}
            couponConditionAmount={modalContent?.couponConditionAmount}
            errorCode={modalContent?.code}
            errorMessage={modalContent?.message}
            errorTitle={modalContent?.title}
            couponType={selectCoupon?.couponSubType}/>
        </Suspense>
      }
    </>
  )
})

export default MerchantCoupons
